import React, { useState, useEffect, useRef } from 'react';
import {
  DictationController,
  isCancelPhrase,
  isStopPhrase,
  stripControlPhrases,
} from '../services/DictationController';

interface WakeWordDetectorProps {
  accessKey: string;
  onWakeWordDetected: () => void;
  onVoiceCommand?: (command: string) => void;
  isEnabled: boolean;
  onStatusChange?: (status: 'initializing' | 'listening' | 'stopped' | 'error') => void;
}

const WakeWordDetector: React.FC<WakeWordDetectorProps> = ({
  accessKey,
  onWakeWordDetected,
  onVoiceCommand,
  isEnabled,
  onStatusChange
}) => {
  const [status, setStatus] = useState<'initializing' | 'listening' | 'stopped' | 'error'>('stopped');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);
  const retryCountRef = useRef(0);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxRetries = 3;
  // Command mode: active for 8s after wake word is heard. Only then do we pass
  // subsequent utterances to onVoiceCommand. Without this, every background sound
  // triggers "I didn't catch that."
  const commandModeRef = useRef(false);
  const commandModeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const activateCommandMode = () => {
    commandModeRef.current = true;
    if (commandModeTimeoutRef.current) clearTimeout(commandModeTimeoutRef.current);
    commandModeTimeoutRef.current = setTimeout(() => {
      commandModeRef.current = false;
    }, 8000);
  };

  const deactivateCommandMode = () => {
    commandModeRef.current = false;
    if (commandModeTimeoutRef.current) clearTimeout(commandModeTimeoutRef.current);
  };

  const updateStatus = (newStatus: typeof status) => {
    setStatus(newStatus);
    onStatusChange?.(newStatus);
  };

  useEffect(() => {
    const initializeWakeWord = async () => {
      if (!isEnabled) {
        cleanup();
        return;
      }

      try {
        updateStatus('initializing');
        setError(null);

        // Use browser's Web Speech API for wake word detection
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
          throw new Error('Speech recognition not supported in this browser');
        }

        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: any) => {
          const rawTranscript = event.results[event.results.length - 1][0].transcript.trim();
          const transcript = rawTranscript.toLowerCase();

          // While dictating, speech is note content — not commands. Only an
          // explicit stop or cancel phrase gets out of this branch, so ordinary
          // clinical speech ("we agreed she would stop the medication") cannot
          // end the note by accident.
          if (DictationController.isActive()) {
            const ending = isStopPhrase(rawTranscript);
            const cancelling = isCancelPhrase(rawTranscript);

            // Keep whatever was said before the command in the same breath —
            // recognition often delivers "...weekly stop dictation" as one result.
            if (!cancelling) {
              const spoken = ending ? stripControlPhrases(rawTranscript) : rawTranscript;
              if (spoken) DictationController.appendSegment(spoken);
            }

            if (cancelling) {
              DictationController.cancel();
            } else if (ending) {
              DictationController.stop();
            }
            return;
          }

          // Check for wake words first
          const wakeWords = ['hey clinic', 'clinic', 'picovoice', 'hey voice'];
          const hasWakeWord = wakeWords.some(w => transcript.includes(w));

          if (hasWakeWord) {
            // Strip wake word to get the actual command
            let command = transcript;
            for (const w of wakeWords) {
              command = command.replace(w, '').trim();
            }

            if (command.length > 2 && onVoiceCommand) {
              // Wake word + command in same utterance (e.g. "hey clinic go to HR")
              deactivateCommandMode();
              onVoiceCommand(command);
            } else {
              // Just the wake word — activate command mode, greet, wait for next utterance
              activateCommandMode();
              onWakeWordDetected();
            }
          } else if (commandModeRef.current && onVoiceCommand) {
            // Follow-up utterance after wake word — process as command
            deactivateCommandMode();
            onVoiceCommand(transcript);
          }
          // else: utterance with no wake word and not in command mode — ignore silently
        };

        recognition.onstart = () => {
          isListeningRef.current = true;
          retryCountRef.current = 0; // Reset retry count on successful start

          // Clear initialization timeout since we started successfully
          if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
            initTimeoutRef.current = null;
          }

          updateStatus('listening');
        };

        recognition.onend = () => {
          isListeningRef.current = false;
          // Auto-restart if still enabled
          if (isEnabled && recognitionRef.current) {
            setTimeout(() => {
              if (isEnabled && recognitionRef.current && !isListeningRef.current) {
                try {
                  recognition.start();
                } catch (err) {
                  console.error('Auto-restart failed:', err);
                  updateStatus('error');
                  setError('Failed to restart speech recognition');
                }
              }
            }, 1000);
          } else {
            updateStatus('stopped');
          }
        };

        recognition.onerror = (event: any) => {
          console.error('❌ Speech recognition error:', event.error);

          // Clear initialization timeout on error
          if (initTimeoutRef.current) {
            clearTimeout(initTimeoutRef.current);
            initTimeoutRef.current = null;
          }

          if (event.error === 'not-allowed') {
            setError('Microphone access denied. Please allow microphone access and refresh the page.');
            updateStatus('error');
          } else if (event.error === 'no-speech') {
            return; // Don't show error for no-speech
          } else if (event.error === 'aborted') {
            // Try to restart automatically
            if (retryCountRef.current < maxRetries && isEnabled) {
              retryCountRef.current++;
              setTimeout(() => {
                if (isEnabled && !isListeningRef.current) {
                  try {
                    recognition.start();
                  } catch (err) {
                    console.error('Retry failed:', err);
                  }
                }
              }, 1000);
            } else {
              setError('Speech recognition failed to start. Click Start to try again.');
              updateStatus('error');
            }
          } else {
            setError(`Speech recognition error: ${event.error}`);
            updateStatus('error');
          }
        };

        recognitionRef.current = recognition;

        // Start immediately with better error handling
        try {
          recognition.start();

          // Set a timeout to prevent getting stuck in initializing state
          initTimeoutRef.current = setTimeout(() => {
            if (status === 'initializing' && !isListeningRef.current) {
              setError('Initialization timeout. Please check microphone permissions and try again.');
              updateStatus('error');
            }
          }, 5000); // 5 second timeout

        } catch (err) {
          console.error('Initial start failed:', err);
          setError('Failed to start speech recognition. Please check microphone permissions.');
          updateStatus('error');
        }

      } catch (err: any) {
        console.error('Failed to initialize wake word detection:', err);
        setError(err.message || 'Failed to initialize wake word detection');
        updateStatus('error');
      }
    };

    if (isEnabled) {
      initializeWakeWord();
    } else {
      cleanup();
    }

    return () => {
      cleanup();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, onWakeWordDetected]);

  const cleanup = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      // Clear initialization timeout
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }

      // Clear command mode
      deactivateCommandMode();

      isListeningRef.current = false;
      updateStatus('stopped');
    } catch (err) {
      console.error('Cleanup error:', err);
    }
  };

  const toggleListening = () => {
    if (status === 'listening') {
      cleanup();
    } else if (status === 'stopped' || status === 'error') {
      // Re-initialize
      setError(null);
      updateStatus('initializing');
      // Force re-initialization by briefly toggling isEnabled state
      setTimeout(() => {
        if (recognitionRef.current) {
          recognitionRef.current.start();
        }
      }, 100);
    }
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      background: status === 'listening' ? '#D4A017' : status === 'error' ? '#ef4444' : '#6b7280',
      borderRadius: '0.5rem',
      color: 'white',
      fontSize: '0.875rem'
    }}>
      {/* Status Indicator */}
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: 'white',
        animation: status === 'listening' ? 'pulse 2s infinite' : 'none'
      }} />

      {/* Status Text */}
      <span style={{ fontWeight: '500' }}>
        {status === 'initializing' && 'Initializing...'}
        {status === 'listening' && 'Listening for "Hey Clinic"'}
        {status === 'stopped' && 'Wake Word Stopped'}
        {status === 'error' && 'Wake Word Error'}
      </span>

      {/* Toggle Button */}
      <button
        onClick={toggleListening}
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          border: 'none',
          borderRadius: '0.25rem',
          color: 'white',
          padding: '0.25rem 0.5rem',
          fontSize: '0.75rem',
          cursor: 'pointer',
          marginLeft: '0.5rem'
        }}
      >
        {status === 'listening' ? 'Stop' : 'Start'}
      </button>

      {/* Error Display */}
      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem',
          background: '#fef2f2',
          color: '#dc2626',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 10
        }}>
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default WakeWordDetector;